import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useSupabaseClient, useUser } from "@supabase/auth-helpers-react";
import Cookies from "js-cookie";
import { toast } from "sonner";
import { getLogoImage } from "../../utils/getLogoImage";
import getThemeColors from "../../utils/getColorTheme";
import { useTheme } from "../../contexts/ThemeContext";
import useAnalytics from "../../hooks/useAnalytics";
import CloseSVG from '../../assets/images/icons/close.svg'

const MenuMobile = ({ setOpenMenuMobile }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [userFullName, setUserFullName] = useState("");
  const navigate = useNavigate();
  const supabaseClient = useSupabaseClient();
  const user = useUser();
  const {addTheme, clearWithTheme} = useTheme();
  const logo = getLogoImage(addTheme);
  const { theme, background } = getThemeColors(addTheme);
  const { trackConfirmationAlertClick } = useAnalytics();
  
  // Calculate initials from the fetched full name
  const nameParts = userFullName?.split(" ");
  const initials = nameParts?.slice(0, 2).map(n => n[0]).join("") || "??";

  // Fetch user's full name from database
  useEffect(() => {
    if (user?.id) {
      supabaseClient
        .from("users")
        .select("full_name")
        .eq("id", user.id)
        .single()
        .then(({ data }) => {
          if (data) {
            setUserFullName(data.full_name || "");
          }
        });
    }
  }, [user?.id, supabaseClient]);

  // Listen for name updates
  useEffect(() => {
    const handleNameUpdated = (e) => {
      if (e?.detail) {
        setUserFullName(e.detail);
      }
    };

    const handleStorage = (e) => {
      if (e.key === "userFullName") {
        setUserFullName(e.newValue);
      }
    };

    window.addEventListener("name-updated", handleNameUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("name-updated", handleNameUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const logout = async () => {
    toast.message("Do you really want to log out?", {
      duration: 10000,
      style: {
        width: "100%",
        maxWidth: 350,
      },
      action: {
        label: "Yes",
        onClick: async () => {
          // Track logout click
          trackConfirmationAlertClick('logout', true);

          const cookies = Object.keys(Cookies.get());
          cookies.forEach(cookie => {
            Cookies.remove(cookie);
          });

          sessionStorage.clear();
          clearWithTheme();

          await supabaseClient.auth.signOut();

          navigate("/about");
        },
      },
      cancel: {
        label: "No",
        onClick: () => {
          // Track confirmation alert action - user clicked No
          trackConfirmationAlertClick('logout', false);
        },
      },
    });
  };
  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      setOpenMenuMobile(false);
    }, 500);
  };

  const navigationLinks = [
    {
      title: "Chat",
      action: () => navigate("/chat"),
    }
  ];

  return (
     <div
      className={`mobile-menu ${isVisible ? "animate__fadeInDown" : "animate__fadeOutUp"}`}
      onAnimationEnd={() => {
        if (!isVisible) setOpenMenuMobile(false)
      }}
    >
      <div
        className="mobile-menu__container"
        style={{
          backgroundColor: background,
          boxShadow: `0px 0px 0px 0.15px ${theme}`,
        }}
      >
        {/* Header with logo and close button */}
        <div className="mobile-menu__header">
          <div className="mobile-menu__logo" style={{ color: theme }}>
            FAM
          </div>
          <div className="mobile-menu__actions">
            <div className="mobile-menu__avatar" style={{ backgroundColor: "#F8D675" }}>
              <span style={{ color: theme }}>{initials}</span>
            </div>
            <button className="mobile-menu__close" type="button" onClick={handleClose} aria-label="Close menu">
              <img src={CloseSVG} alt="closebutton" />
            </button>
          </div>
        </div>

        {/* Navigation links */}
        <div className="mobile-menu__nav">
          <ul className="mobile-menu__links">
            {navigationLinks.map((item, index) => (
              <li key={index} className="mobile-menu__link-item">
                <button
                  onClick={() => {
                    item.action()
                    setOpenMenuMobile(false)
                  }}
                  className="mobile-menu__link-button"
                  style={{ color: theme }}
                  aria-label={`Navigate to ${item.title}`}
                >
                  {item.title}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Logout button at the bottom */}
        <div className="mobile-menu__footer">
          <button
            onClick={logout}
            className="mobile-menu__logout"
            style={{
              borderColor: theme,
              color: theme,
            }}
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default MenuMobile;
